import React, { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { WelletContext } from '../../context/WelletContext';

import { useTranslation } from 'react-i18next';
import styles from './Commissions.module.css';
import ContentApp from '../../components/ContentApp';
import Header from '../../components/Common/Header/Header';
import { formatMoney } from '../../helpers/format';
import { CircleSpinner } from 'react-spinners-kit';

const Commissions = () => {
  const { t } = useTranslation();
  const currency = useSelector((state) => state.currency.default);
  const welletContext = useContext(WelletContext);
  const [loading, setLoading] = useState(true);
  const [prizes, setPrizes] = useState(null);
  const [prizeFirstSale, setPrizeFirstSale] = useState(null);
  const [prizeSaleAmountPerPax, setPrizeSaleAmountPerPax] = useState(null);

  const getCommissions = async () => {
    setLoading(true);
    try {
      const response = await welletContext.apis.tickets.get(`associates/commission`);
      if (response.status === 200) {
        setPrizes(response.data.sellerGoals);
        setPrizeFirstSale(response.data.sellerGoals.find((goal) => goal.goalType === 'FIRST_SALE'));
        setPrizeSaleAmountPerPax(
          response.data.sellerGoals.find(
            (goal) => goal.goalType === 'SALE' && goal.prizePaymentType === 'AMOUNT_PER_PAX',
          ),
        );
      } else {
        console.error('Failed to fetch data', response);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCommissions();
  }, []);

  return (
    <>
      <Header />
      <ContentApp>
        {loading ? (
          <div className='loading-app' style={{ zIndex: 1000 }}>
            <div className='loaderDiv'>
              <CircleSpinner
                className='spinner-app mx-auto'
                style={{ margin: 'auto' }}
                size={40}
                color='var(--color-font-primary)'
              />
            </div>
          </div>
        ) : (
          <div>
            <div className='text-tertiary font-125 fw-700'>
              {t('commissionsExplanation.howItWorks')}
            </div>
            <div className='text-tertiary font-small fw-700 mt-3'>
              {t('commissionsExplanation.generalCommission')}
            </div>
            <div className='text-secondary font-small mt-2'>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('commissionsExplanation.generalCommissionDescription'),
                }}
              />
            </div>

            <div className={styles.exampleBox}>
              <div className='font-extra-small fw-700'>{t('example')}</div>
              <div className='font-small'>
                {t('commissionsExplanation.generalCommissionExample', {
                  amount: formatMoney(100, {
                    currencySymbol: currency?.symbol,
                    currencyShortForm: currency?.shortForm,
                  }),
                  commission: 50,
                })}
              </div>
              <div
                className='font-small mt-3'
                dangerouslySetInnerHTML={{
                  __html: t('commissionsExplanation.generalCommissionExample2', {
                    amount: formatMoney(50, {
                      currencySymbol: currency?.symbol,
                      currencyShortForm: currency?.shortForm,
                    }),
                  }),
                }}
              ></div>
            </div>

            {prizeFirstSale && (
              <div className='mt-4 pt-3 border-top'>
                <div className='text-tertiary font-125 fw-700'>
                  {t('commissionsExplanation.FIRST_SALE_FIXED_AMOUNT_title')}
                </div>
                <div
                  className='text-secondary font-small mt-1'
                  dangerouslySetInnerHTML={{
                    __html: t('commissionsExplanation.FIRST_SALE_FIXED_AMOUNT_description', {
                      amount: formatMoney(prizeFirstSale.prizeValue, {
                        currencySymbol: currency?.symbol,
                        currencyShortForm: currency?.shortForm,
                      }),
                    }),
                  }}
                >
                  {}
                </div>
              </div>
            )}

            {prizeSaleAmountPerPax && (
              <div className='mt-4 pt-3 border-top'>
                <div className='text-tertiary font-125 fw-700'>
                  {t('commissionsExplanation.SALE_AMOUNT_PER_PAX_title')}
                </div>
                <div className='text-tertiary font-small mt-2'>
                  {t('commissionsExplanation.SALE_AMOUNT_PER_PAX_description')}
                </div>

                <ul className='mt-3 pl-3'>
                  <li
                    className='text-tertiary font-small'
                    dangerouslySetInnerHTML={{
                      __html: t('commissionsExplanation.SALE_AMOUNT_PER_PAX_item1', {
                        amount: formatMoney(50, {
                          currencySymbol: currency?.symbol,
                          currencyShortForm: currency?.shortForm,
                        }),
                      }),
                    }}
                  ></li>
                  <li
                    className='text-tertiary font-small mt-3'
                    dangerouslySetInnerHTML={{
                      __html: t('commissionsExplanation.SALE_AMOUNT_PER_PAX_item2', {
                        amount: formatMoney(prizeSaleAmountPerPax.prizeValue, {
                          currencySymbol: currency?.symbol,
                          currencyShortForm: currency?.shortForm,
                        }),
                      }),
                    }}
                  ></li>
                </ul>

                {/* <div className='text-secondary font-small mt-2'>
                  {t('commissionsExplanation.SALE_AMOUNT_PER_PAX_description2', {
                    amount: formatMoney(prizeSaleAmountPerPax.prizeValue, {
                      currencySymbol: currency?.symbol,
                      currencyShortForm: currency?.shortForm,
                    }),
                  })}
                </div> */}

                <div className={styles.exampleBox + ' text-primary font-tiny fw-500 mt-3'}>
                  {t('commissionsExplanation.SALE_AMOUNT_PER_PAX_description3')}
                </div>
              </div>
            )}

            <div className='mt-4 pt-4 border-top'>
              <div className='card p-3'>
                <div className='text-primary fw-700'>
                  {t('commissionsExplanation.commissionAndBonusSummary')}
                </div>
                <ul className='font-small mt-3 ml-2 pl-3 mb-0'>
                  <li
                    dangerouslySetInnerHTML={{
                      __html: t('commissionsExplanation.commissionSchemaPercentage', {
                        commission: 50,
                      }),
                    }}
                  />

                  {prizes &&
                    prizes
                      .filter((prize) => prize.goalType === 'FIRST_SALE')
                      .map((prize) => (
                        <li
                          className='mt-3'
                          dangerouslySetInnerHTML={{
                            __html: t(
                              'commissionsExplanation.commissionSchema_' +
                                prize.goalType +
                                '_' +
                                prize.prizePaymentType,
                              {
                                amount: formatMoney(prize.prizeValue, {
                                  currencySymbol: currency?.symbol,
                                  currencyShortForm: currency?.shortForm,
                                }),
                              },
                            ),
                          }}
                        />
                      ))}

                  <li
                    className='mt-3'
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'commissionsExplanation.commissionSchema_SALE_AMOUNT_FIRST_SALE_USER',
                        {
                          amount: formatMoney(50, {
                            currencySymbol: currency?.symbol,
                            currencyShortForm: currency?.shortForm,
                          }),
                          amount_pax: formatMoney(2, {
                            currencySymbol: currency?.symbol,
                            currencyShortForm: currency?.shortForm,
                          }),
                        },
                      ),
                    }}
                  />

                  <li
                    className='mt-4'
                    dangerouslySetInnerHTML={{
                      __html: t('commissionsExplanation.commissionAndBonusSummaryText1'),
                    }}
                  />
                  <li
                    className='mt-3'
                    dangerouslySetInnerHTML={{
                      __html: t('commissionsExplanation.commissionAndBonusSummaryText2'),
                    }}
                  />
                </ul>
              </div>
            </div>
          </div>
        )}
      </ContentApp>
    </>
  );
};

export default Commissions;
